import "./App.css";
import { useState, useRef, useEffect } from "react";
import Loader from "./components/loader";
import LocBlock from "./components/locblock";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { environment } from "./EnvDev";
import UsaMap from "./components/usa-map";

function App() {
	const [loading, setLoading] = useState(false);
	const [locs, setLocs] = useState([]);
	const [notFound, setNotFound] = useState(false);
	const [zip, setZip] = useState("");
	mapboxgl.accessToken = environment.mapbox.accessToken;
	const mapContainerRef = useRef(null);
	const map = useRef(null);
	const [lng] = useState(-95.7129);
	const [lat] = useState(37.0902);
	const [zoom] = useState(2);

	const handleSubmission = (e) => {
		e.preventDefault();
		setLocs([]);
		setNotFound(false);
		setLoading(true);
		geoLocate(zip);
	};

	const geoLocate = async (zip) => {
		const call = await fetch(
			`https://api.mapbox.com/geocoding/v5/mapbox.places-permanent/${zip}.json?access_token=${environment.mapbox.accessToken}&country=US`
		);
		const response = await call.json();

		if (response.features.length === 0) {
			setNotFound(true);
			setLoading(false);
			return;
		}

		const coords = response.features[0].geometry.coordinates;
		const [lat, long] = [coords[1], coords[0]];
		getCloserLocs(lat, long);
	};

	const getCloserLocs = async (lat, long) => {
		const call = await fetch(
			`https://www.advancedturf.com/wp-json/locs/closer?lat=${lat}&lng=${long}`
		);
		const response = await call.json();
		setLoading(false);
		if (response.length === 0) {
			setNotFound(true);
			return;
		}
		setLocs(response);
	};

	useEffect(() => {
		// Parse the URL query parameters
		const searchParams = new URLSearchParams(window.location.search);
		const zipParam = searchParams.get("zip"); // Get the value of the 'zip' parameter

		if (zipParam) {
			setZip(zipParam); // Set the input value to the 'zip' parameter
			geoLocate(zipParam); // Trigger geolocation and subsequent actions
		}
	}, []);

	const setMap = () => {
		map.current = new mapboxgl.Map({
			container: mapContainerRef.current,
			style: "mapbox://styles/mapbox/streets-v11",
			center: [lng, lat],
			zoom: zoom,
		});
		map.current.addControl(new mapboxgl.NavigationControl(), "top-right");
		let bounds = new mapboxgl.LngLatBounds();
		setMarkers(bounds);
	};

	useEffect(() => {
		if (locs.length > 0) {
			setMap();
		}
	}, [locs.length]);

	const setMarkers = (bounds) => {
		locs.forEach((marker) => {
			const el = document.createElement("div");
			el.className = "marker";
			el.innerHTML = `<div class="text-center w-150px"><span class="inline-block px-2 py-1 mb-1 text-xs font-bold text-white bg-green-800">${marker.name}</span><br/><svg class="inline-block" width="40" height="42" viewBox="0 0 18 21" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path fill-rule="evenodd" clip-rule="evenodd" d="M8.54 20.351L8.61 20.391L8.638 20.407C8.74903 20.467 8.87327 20.4985 8.9995 20.4985C9.12573 20.4985 9.24997 20.467 9.361 20.407L9.389 20.392L9.46 20.351C9.85112 20.1191 10.2328 19.8716 10.604 19.609C11.5651 18.9305 12.463 18.1667 13.287 17.327C15.231 15.337 17.25 12.347 17.25 8.5C17.25 6.31196 16.3808 4.21354 14.8336 2.66637C13.2865 1.11919 11.188 0.25 9 0.25C6.81196 0.25 4.71354 1.11919 3.16637 2.66637C1.61919 4.21354 0.75 6.31196 0.75 8.5C0.75 12.346 2.77 15.337 4.713 17.327C5.53664 18.1667 6.43427 18.9304 7.395 19.609C7.76657 19.8716 8.14854 20.1191 8.54 20.351ZM9 11.5C9.79565 11.5 10.5587 11.1839 11.1213 10.6213C11.6839 10.0587 12 9.29565 12 8.5C12 7.70435 11.6839 6.94129 11.1213 6.37868C10.5587 5.81607 9.79565 5.5 9 5.5C8.20435 5.5 7.44129 5.81607 6.87868 6.37868C6.31607 6.94129 6 7.70435 6 8.5C6 9.29565 6.31607 10.0587 6.87868 10.6213C7.44129 11.1839 8.20435 11.5 9 11.5Z" fill="#006225"/>
			</svg></div>`;
			new mapboxgl.Marker(el).setLngLat(marker.coords).addTo(map.current);
			bounds.extend(marker.coords);
		});

		if (locs.length === 1) {
			map.current.flyTo({
				center: locs[0].coords,
				zoom: 14,
				speed: 1,
				pitch: 45,
				bearing: -20,
			});
		} else {
			const mq = window.matchMedia("(min-width: 1280px)");
			const pad = mq.matches
				? { top: 80, bottom: 140, left: 280, right: 280 }
				: { top: 50, bottom: 50, left: 50, right: 50 };

			setTimeout(() => {
				map.current.fitBounds(bounds, {
					padding: pad,
					speed: 1,
					pitch: 45,
					bearing: -20,
					antialias: true,
				});
			}, 1400);
		}
	};

	const handleInputChange = (event) => {
		setZip(event.target.value); // Update the state when the input value changes
	};

	return (
		<div className="container px-4 pt-10 pb-4 mx-auto w-full">
			<div className="gap-6 items-center lg:grid lg:grid-cols-12">
				<div className="lg:col-span-5">
					<h1 className="mb-2 text-3xl font-bold md:text-5xl">
						Find An ATS
						<br />
						Location Near You
					</h1>
					<p>
						Please enter your zip code to find an Advanced Turf Solutions
						<br />
						location near you.
					</p>
					{locs.length === 0 && (
						<form id="locator-zip">
							<div className="block relative mb-6 w-full zip">
								<input
									className="w-full lg:max-w-[400px] py-3 pl-4 pr-8 border border-gray-100 mt-6 xl:max-w-[500px] bg-gray-100 focus:outline-none focus:border-gray-300 duration-200 transition-all text-gray-600"
									type="text"
									autoComplete="off"
									id="zip"
									aria-label="zip"
									onChange={handleInputChange}
									value={zip}
									placeholder="Enter your zip code"
								/>
								{loading && <Loader />}
							</div>

							<button
								type="submit"
								onClick={handleSubmission}
								className="w-full button button--primary lg:w-auto inline-block text-white py-3 px-14 text-sm font-semibold rounded transition-all duration-200 capitalize eff-a__button my-1 bg-[#215732]">
								{loading ? "Locating" : "Search"}
							</button>
						</form>
					)}

					{notFound && (
						<div className="p-5 mt-10 border border-gray-600">
							An ATS store cannot be found within 150 miles of your location.
							Service may still be available in your area. Please use our{" "}
							<a className="underline underline-offset-4" href="/find-your-rep/">
								Sales Rep Finder Tool
							</a>{" "}
							to find a distribution representative to help you.
						</div>
					)}
				</div>
				{locs.length === 0 && (
					<div className="mt-10 lg:col-span-7">
						<UsaMap />
					</div>
				)}
				{locs.length > 0 && (
					<div className="block results lg:col-span-7">
						<form id="locator-zip" className="locatorZipForm">
							<div className="inline-block relative w-full zip">
								<input
									className="py-3 pr-8 pl-4 mt-6 mb-6 w-full text-gray-600 bg-gray-100 border border-gray-100 transition-all duration-200 focus:outline-none focus:border-gray-300 lg:mb-0 lg:mt-0"
									type="text"
									autoComplete="off"
									value={zip}
									onChange={handleInputChange}
									id="zip"
									aria-label="zip"
									placeholder="Try a different zip code"
								/>
								{loading && <Loader />}
							</div>

							<button
								type="submit"
								onClick={handleSubmission}
								className="w-full button button--primary lg:w-auto inline-block text-white py-3 px-14 text-sm font-semibold rounded transition-all duration-200 capitalize eff-a__button my-1 bg-[#215732]">
								{loading ? "Locating" : "Search"}
							</button>
						</form>
					</div>
				)}
			</div>
			{locs.length > 0 && (
				<div className="grid grid-cols-6 gap-x-10 my-14">
					<div className="col-span-6 results md:col-span-3 xl:col-span-2">
						<span className="block mt-10 mb-8 md:mt-0 resultsnumber">
							We've found {locs.length} Nearby Locations
						</span>
						<div className="locs-list">
							{locs.map((loc, index) => (
								<LocBlock loc={loc} key={index} />
							))}
						</div>
						<span className="text-sm text-gray-500">
							Area Searched: 150 Mile Radius
						</span>
					</div>
					<div
						className="hidden md:col-span-3 xl:col-span-4 md:block map-container"
						ref={mapContainerRef}></div>
				</div>
			)}
		</div>
	);
}

export default App;
